import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import Papa from 'papaparse';
import { EmptyState } from '@/components/page-header';
import { VatReportShell, rangeSearch } from '@/components/vat-report';
import { api } from '@/lib/api';
import { formatTrn } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useT } from '@/lib/i18n';
import { downloadCsv } from '@/lib/vat';

interface VatSummary {
  orgName: string;
  trn: string | null;
  from: string;
  to: string;
  currency: string;
  /** Boxes 1a–14 in order; amount null when nothing reports to the box. */
  boxes: Array<{ box: string; label: string; amount: string | null; total: boolean }>;
  totals: { netVat: string };
  generatedAt: string;
}

export const Route = createFileRoute('/reports/vat-summary')({
  component: VatSummaryReport,
  validateSearch: rangeSearch,
});

/**
 * VAT summary (R2 VAT category) in the box layout UAE accountants know: VAT by kind of supply in
 * boxes 1a–5, totals in 6, 9 and 10, net sales and purchases in 11 and 12. Empty boxes are blank.
 */
function VatSummaryReport() {
  const orgId = useOrgId();
  const t = useT();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery({
    queryKey: ['vat', orgId, 'report', 'summary', search.from, search.to],
    queryFn: () =>
      api<VatSummary>(`/orgs/${orgId}/vat/reports/summary?from=${search.from}&to=${search.to}`),
    enabled: Boolean(orgId),
  });
  const d = q.data;
  const exportCsv = () => {
    if (!d) return;
    const rows = [['', 'TOTAL'], ...d.boxes.map((b) => [b.label, b.amount ?? ''])];
    downloadCsv(`VAT summary ${search.from} to ${search.to}.csv`, Papa.unparse(rows));
  };
  return (
    <VatReportShell
      title={t('reportPages.dashboard.vatReports.summaryTitle')}
      orgName={d?.orgName}
      trn={d ? formatTrn(d.trn) : null}
      from={search.from}
      to={search.to}
      onRun={(r) => void navigate({ search: r })}
      onExport={d ? exportCsv : undefined}
      generatedAt={d?.generatedAt}
    >
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : q.isError ? (
        <EmptyState
          title={t('reportPages.dashboard.vatReports.loadError')}
          hint={q.error.message}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-foreground text-xs uppercase tracking-wide">
                <th className="py-2 text-start font-medium" />
                <th className="w-[160px] py-2 text-end font-medium">
                  {t('reportPages.dashboard.vatReports.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {d!.boxes.map((b) => (
                <tr
                  key={b.box}
                  className={b.total ? 'border-t font-semibold' : undefined}
                  data-testid={`vat-box-${b.box}`}
                >
                  <td className="py-1.5 pe-4">{b.label}</td>
                  <td
                    className="tnum py-1.5 text-end"
                    {...(b.box === '10' ? { 'data-testid': 'vat-summary-net' } : {})}
                  >
                    {b.amount === null
                      ? ''
                      : b.total
                        ? `${d!.currency} ${formatAmount(b.amount)}`
                        : formatAmount(b.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-foreground">
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </VatReportShell>
  );
}
